import { createSlice } from '@reduxjs/toolkit';
const initialState = {
	loginLoading: false,
	getProfileLoading: false,
	error: null,

	selectedProfile: {},
	userPermissions: [],
	profileLoadingComplete: false,
	// Boot only: { code, orgID } when the server refused the chosen org before any profile loaded.
	// Once a profile exists the refusal lives on it (selectedProfile.accessRefusal).
	accessRefusal: null,

	isLoggedIn: false,
	switchProfileLoading: false,
	signupLoading: false,
	// True from the moment sign-out starts until the session is actually gone. Navigation holds a
	// steady loader on it so we never route to a lazy screen the browser is about to navigate off.
	signingOut: false,
	// Which door this session came through. Sign-out reads it instead of asking the Kinde SDK,
	// which answers with a token round-trip we do not need when we are ending the session anyway.
	providerID: null
};

// Create Auth reducer slice
const authSlice = createSlice({
	name: 'auth',
	initialState: initialState,
	reducers: {
		checkAuthorization: (state) => {
			state.loginLoading = true;
			state.error = null;
		},
		login: (state) => {
			state.loginLoading = true;
		},
		loginSuccess: (state) => {
			state.loginLoading = false;
			state.isLoggedIn = true;
		},
		loginError: (state, action) => {
			state.loginLoading = false;
			state.error = action.payload;
		},
		getUserProfile: (state) => {
			state.getProfileLoading = true;
			state.error = null;
		},
		getUserProfileSuccess: (state, action) => {
			state.getProfileLoading = false;
			// Preserve the current siteID so a mid-session profile refresh
			// doesn't reset a site-admin back to 'all' (which nukes scoped buckets).
			const currentSiteID = state.selectedProfile?.siteID;
			state.selectedProfile = {
				...action.payload.selectedProfile,
				siteID: currentSiteID || 'all'
			};
			state.userPermissions = action.payload.userPermissions;
			state.profileLoadingComplete = true;
			state.accessRefusal = null;
		},
		getUserProfileError: (state, action) => {
			state.getProfileLoading = false;
			state.error = action.payload;
		},
		// 403 PERSON_DISABLED / OUTSIDE_VALIDITY: on boot Navigation shows AccessRefused;
		// mid-session the profile drops every role and the shell shows AccountDisabled.
		getUserProfileRefused: (state, action) => {
			state.getProfileLoading = false;
			if (state.profileLoadingComplete) {
				state.selectedProfile = { ...state.selectedProfile, roles: {}, accessRefusal: action.payload };
			} else {
				state.accessRefusal = action.payload;
			}
		},
		// The bootstrap ran BEFORE the workspace list landed, so there was nothing to resolve and
		// nothing went wrong — the saga bailed out rather than reading an empty list as "no orgs".
		// Releases the loading flag WITHOUT writing an error, deliberately: the workspace error is
		// the accurate one and the loader reads `Auth.error` first, so writing one here would hide it.
		getUserProfileDeferred: (state) => {
			state.getProfileLoading = false;
		},
		// Same reset as before — every caller still gets a signed-out store synchronously — plus the
		// flag that keeps Navigation from mounting /signin during a provider redirect.
		//
		// 🛑 `providerID` SURVIVES THIS RESET, and must. redux-saga hands an action to the reducers
		// BEFORE emitting it to the saga channel, so the `select` in the logout saga reads the state
		// this returns — spreading `initialState` over it made that select resolve null every time
		// and the provider handoff dead on arrival. `logoutComplete` is where it finally clears.
		logout: (state) => {
			return { ...initialState, signingOut: true, providerID: state.providerID };
		},
		// The session ended underneath the user. Identical teardown to `logout` (see why `providerID`
		// survives, above); separate only so the saga can mark it and a redux trace names it.
		sessionExpired: (state) => {
			return { ...initialState, signingOut: true, providerID: state.providerID };
		},
		// Recorded once, when the session is established.
		setProvider: (state, action) => {
			state.providerID = action.payload;
		},
		// The provider finished WITHOUT taking the page away (the Firebase path). Release the loader
		// so the user lands on sign-in.
		logoutComplete: () => {
			return initialState;
		},
		switchProfile: (state) => {
			state.switchProfileLoading = true;
			state.error = null;
		},
		switchProfileSuccess: (state, action) => {
			state.switchProfileLoading = false;
			state.selectedProfile = { ...action.payload.selectedProfile, siteID: 'all' };
		},
		switchProfileError: (state, action) => {
			state.switchProfileLoading = false;
			state.error = action.payload;
		},
		signupUser: (state) => {
			state.signupLoading = true;
			state.error = null;
		},
		signupUserSuccess: (state) => {
			state.signupLoading = false;
			state.isLoggedIn = true;
		},
		signupUserError: (state, action) => {
			state.signupLoading = false;
			state.error = action.payload;
		},
		resetError: (state) => {
			if (state.error) state.error = null;
		}
	},
	extraReducers: (builder) => {
		// 🛑 No `auth/logout` case here. RTK spreads slice case reducers OVER extraReducers, so the
		// slice's own `logout` always won and this never ran — and it now claimed the opposite of what
		// that reducer does, which would tell a reader `providerID` is wiped when it deliberately is not.
		builder.addCase('app/setSelectedSite', (state, action) => {
			state.selectedProfile.siteID = action.payload ?? 'all';
		});
	}
});

// Export actions
export const authActions = authSlice.actions;

// Export reducer
export default authSlice.reducer;
